import { supabase } from "./supabase";

/**
 * O IO das telas do Sentinela (000397, fase 3a) — ZERO regra de acesso e nenhum `select`: as tabelas
 * `sentinela_*` têm `revoke all`, e quem decide o escopo (até a virada, só quem vê a aba Sentinela, com o acervo
 * inteiro), a ordem dos apontamentos, a análise que explica cada um, se o link pode virar `href` e se o PDF
 * existe é o BANCO. Aqui ficam os tipos, as quatro leituras, o link do PDF e a APRESENTAÇÃO dos valores de lá
 * (os rótulos e as máscaras de `badges.tsx` e `format.ts` do app de lá, com os defeitos dele consertados onde
 * a tela os mostrava: a data no fuso do navegador e a situação da varredura crua, em inglês).
 */

export type Resposta = { ok: false; motivo?: string; detalhe?: string };
export type Dono = { nome: string | null; email: string | null } | null;

export type Risco = "clean" | "low" | "medium" | "high" | "unknown";
export type Severidade = "high" | "medium" | "low" | "info";
export type Criticidade = "critical" | "high" | "medium" | "low";
export type Frequencia = "monthly" | "quarterly" | "semiannual" | "none";
export type SituacaoDoAchado = "open" | "confirmed" | "dismissed" | "resolved";
export type Decisao = "ATRIBUIDA" | "NAO_ATRIBUIDA" | "INCONCLUSIVA" | "ATRIBUIDA_POR_ANALISTA";

type BaseDoAlvo = {
  id: string;
  criticidade: Criticidade;
  frequencia: Frequencia;
  monitorado: boolean;
  nivel_de_risco: Risco;
  ultima_varredura_em: string | null;
  proxima_varredura_em: string | null;
  arquivado_em: string | null;
  criado_em: string;
};

export type EmpresaDaLista = BaseDoAlvo & {
  cnpj: string;
  razao_social: string | null;
  nome_fantasia: string | null;
  situacao_cadastral: string | null;
  municipio: string | null;
  uf: string | null;
  vencida: boolean;
  dono: Dono;
};
export type CpfDaLista = BaseDoAlvo & { cpf: string; nome: string; vencida: boolean; dono: Dono };

export type Painel = {
  ok: true;
  escopo: "equipe" | "aluno";
  empresas: EmpresaDaLista[];
  cpfs: CpfDaLista[];
  numeros_empresas: { ativas: number; risco_alto: number; vencidas: number; arquivadas: number };
  numeros_cpfs: { ativos: number; risco_alto: number; vencidas: number; arquivados: number };
  alertas_nao_lidos: number;
  alertas_nao_lidos_cpf: number;
};

export type Analise = {
  decisao: Decisao | string;
  decisao_original: string | null;
  revisado_em: string | null;
  nivel_confianca: number | string | null;
  matriz_compatibilidade: Record<string, unknown> | null;
  justificativa_estruturada: string | null;
  evidencia_trecho: string | null;
  fase_2_risco: Record<string, unknown> | null;
  requer_revisao_humana: boolean;
  nome_do_alvo: string | null;
  url_da_fonte: string | null;
  titulo_da_fonte: string | null;
  veiculo_da_fonte: string | null;
  fonte_publicada_em: string | null;
};

export type Achado = {
  id: string;
  fonte: string;
  categoria: string;
  severidade: Severidade;
  situacao: SituacaoDoAchado;
  ativo: boolean;
  titulo: string;
  descricao: string | null;
  nome_da_entidade: string | null;
  documento_da_entidade: string | null;
  url_de_referencia: string | null;
  motivo_do_descarte: string | null;
  descartado_em: string | null;
  confirmado_em: string | null;
  resolvido_em: string | null;
  visto_primeiro_em: string | null;
  criado_em: string;
  analise: Analise | null;
};

export type Socio = { id: string; nome: string; documento: string | null; qualificacao: string | null; monitorado: boolean };

export type Varredura = {
  id: string;
  situacao: "pending" | "running" | "completed" | "failed";
  gatilho: "manual" | "scheduled";
  achados: number;
  achados_novos: number;
  fontes_consultadas: string[] | null;
  mensagem_de_erro: string | null;
  criado_em: string;
  pdf: string | null;
};

export type AlvoEmpresa = BaseDoAlvo & {
  cnpj: string;
  razao_social: string | null;
  nome_fantasia: string | null;
  situacao_cadastral: string | null;
  cnae_principal: string | null;
  cnae_descricao: string | null;
  municipio: string | null;
  uf: string | null;
  porte: string | null;
  data_abertura: string | null;
  email_de_alerta: string | null;
  chave_de_busca: "nome" | "documento";
};
export type AlvoCpf = BaseDoAlvo & {
  cpf: string;
  nome: string;
  observacoes: string | null;
  email_de_alerta: string | null;
  chave_de_busca: "nome" | "documento";
};

export type Dossie = {
  ok: true;
  escopo: "equipe" | "aluno";
  tipo: "empresa" | "cpf";
  alvo: AlvoEmpresa | AlvoCpf;
  dono: Dono;
  de_outra_pessoa: boolean;
  socios: Socio[] | null;
  achados: Achado[];
  abertos: number;
  alto_aberto: boolean;
  varreduras: Varredura[];
};

export type RefAlvo = { tipo: "empresa" | "cpf"; id: string; nome: string | null } | null;

export type Alerta = {
  id: string;
  titulo: string;
  mensagem: string | null;
  severidade: Severidade;
  lido: boolean;
  email_enviado: boolean;
  email_enviado_em: string | null;
  email_erro: string | null;
  criado_em: string;
  alvo: RefAlvo;
  dono: Dono;
};
export type Alertas = { ok: true; total: number; nao_lidos: number; alertas: Alerta[] };

export type Chamada = {
  id: string;
  fonte: string;
  endpoint: string | null;
  alvo_consultado: string | null;
  status_http: number | null;
  sucesso: boolean;
  duracao_ms: number | null;
  custo_estimado: number | null;
  criado_em: string;
  alvo: RefAlvo;
};
export type Consumo = {
  ok: true;
  numeros: {
    consultas: number;
    sucesso: number;
    total_usd: number;
    pagas: number;
    sem_estimativa: number;
    gratuitas: number;
    desde: string | null;
    fontes: number;
  };
  por_fonte: { fonte: string; consultas: number; custo: number }[];
  chamadas: Chamada[];
};

const FALHA: Resposta = { ok: false, motivo: "falha", detalhe: "Não foi possível falar com o hub. Confira a conexão e tente de novo." };

async function chamar<T>(nome: string, args?: Record<string, unknown>): Promise<T | Resposta> {
  const { data, error } = await supabase.rpc(nome, args ?? {});
  if (error || !data) return FALHA;
  return data as T | Resposta;
}

export const lerPainel = () => chamar<Painel>("sentinela_painel");
export const lerDossie = (tipo: "empresa" | "cpf", id: string) => chamar<Dossie>("sentinela_alvo", { p_tipo: tipo, p_id: id });
export const lerAlertas = () => chamar<Alertas>("sentinela_alertas");
export const lerConsumo = () => chamar<Consumo>("sentinela_consumo");

/** A frase de uma recusa: a do banco, ou a falha de conexão dita como tal. */
export function frase(r: Resposta): string {
  return r.detalhe ?? "Não foi possível ler o Sentinela agora.";
}

/**
 * O endereço assinado do PDF de uma varredura, pedido no CLIQUE e por um minuto. Quem lê o bucket é a policy
 * da 000397 (quem vê a aba Sentinela); sem ela, o pedido volta erro e a tela diz isso.
 */
export async function urlDoPdf(caminho: string): Promise<string | null> {
  const nome = caminho.split("/").pop() ?? "relatorio.pdf";
  const { data, error } = await supabase.storage.from("sentinela-relatorios").createSignedUrl(caminho, 60, { download: nome });
  return error || !data ? null : data.signedUrl;
}

// ---------------------------------------------------------------------------
// A apresentação dos valores de lá
// ---------------------------------------------------------------------------

export const RISCO: Record<string, string> = {
  clean: "Sem apontamentos",
  low: "Risco baixo",
  medium: "Risco médio",
  high: "Risco alto",
  unknown: "Não avaliado",
};
export const SEVERIDADE: Record<string, string> = { high: "Alta", medium: "Média", low: "Baixa", info: "Informativo" };
export const CRITICIDADE: Record<string, string> = { critical: "Crítico", high: "Alto", medium: "Médio", low: "Baixo" };
export const FREQUENCIA: Record<string, string> = {
  monthly: "Mensal",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  none: "Sem recorrência",
};
export const SITUACAO_DO_ACHADO: Record<string, string> = {
  open: "Em aberto",
  confirmed: "Confirmado",
  dismissed: "Descartado (homônimo)",
  resolved: "Não mais detectado",
};
export const DECISAO: Record<string, string> = {
  ATRIBUIDA: "Atribuída",
  NAO_ATRIBUIDA: "Não atribuída",
  INCONCLUSIVA: "Inconclusiva",
  ATRIBUIDA_POR_ANALISTA: "Atribuída por analista",
};
/** Lá a tela mostrava a situação da varredura crua, em inglês ("completed"). */
export const SITUACAO_DA_VARREDURA: Record<string, string> = {
  pending: "pendente",
  running: "em andamento",
  completed: "concluída",
  failed: "falhou",
};
export const GATILHO: Record<string, string> = { manual: "Manual", scheduled: "Recorrente" };
export const CHAVE_DE_BUSCA: Record<string, string> = { nome: "Nome", documento: "Documento" };

/** A matriz de compatibilidade, na ordem e com os rótulos de lá; as chaves desconhecidas vêm depois, cruas. */
export const MATRIZ_LINHAS: [string, string][] = [
  ["documento", "Documento (CPF/CNPJ)"],
  ["nome", "Nome completo"],
  ["profissao_cargo", "Profissão / cargo"],
  ["empresa_setor", "Empresa / setor"],
  ["geografia", "Localização"],
  ["pessoas_relacionadas", "Pessoas relacionadas / sócios"],
];
export const MATRIZ_VALORES: Record<string, string> = {
  COMPATIVEL: "Compatível",
  PARCIALMENTE_COMPATIVEL: "Parcialmente compatível",
  INCOMPATIVEL: "Incompatível",
  CONTRADICAO: "Contradição",
  CONTRADICAO_SEVERA: "Contradição severa",
  SEM_DADOS: "Sem dados",
  ALTA: "Semelhança alta",
  MEDIA: "Semelhança média",
  BAIXA: "Semelhança baixa",
};
export const NATUREZA: Record<string, string> = {
  condenacao: "condenação",
  processo_em_curso: "processo em curso",
  investigacao: "investigação",
  alegacao: "alegação",
  mencao: "menção",
};
export const POLO: Record<string, string> = {
  passivo: "polo passivo (réu ou investigado)",
  ativo: "polo ativo (autor)",
  indeterminado: "polo indeterminado",
};
export const SEVERIDADE_DA_FASE_2: Record<string, string> = { ALTA: "alta", MEDIA: "média", BAIXA: "baixa" };

const FUSO = "America/Sao_Paulo";

/** "04/11/2026 14:05", no relógio de São Paulo (lá era o fuso do navegador). */
export function dataHora(valor: string | null | undefined): string {
  if (!valor) return "—";
  const d = new Date(valor);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, dateStyle: "short", timeStyle: "short" }).format(d);
}

/** "04/11/2026": a data guardada como DIA é lida como texto, sem fuso (a cicatriz do dia antes); o instante, no dia de São Paulo. */
export function data(valor: string | null | undefined): string {
  if (!valor) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(valor);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, dateStyle: "short" }).format(d);
}

const digitos = (v: string | null | undefined) => (v ?? "").replace(/\D/g, "");

/** O CNPJ com máscara; o que não tem 14 dígitos aparece como veio (o sócio mascarado da Receita, "***897908**"). */
export function cnpjNaTela(v: string | null | undefined): string {
  const d = digitos(v);
  if (d.length !== 14) return v || "—";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** O CPF inteiro, com máscara (o do CPF monitorado: lá ele aparece inteiro, de propósito). */
export function cpfNaTela(v: string | null | undefined): string {
  const d = digitos(v);
  if (d.length !== 11) return v || "—";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** O documento de um sócio: o CPF de pessoa física sai mascarado (***.456.789-**), como lá. */
export function documentoDoSocio(v: string | null | undefined): string {
  if (!v) return "documento não informado";
  const d = digitos(v);
  if (d.length === 11) return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
  return cnpjNaTela(v);
}

/** O custo em dólar, como lá: nulo é "?" (sem estimativa), zero é "—", e o centavo vira seis casas. */
export function dolar(v: number | null | undefined): string {
  if (v === null || v === undefined) return "?";
  if (v <= 0) return "—";
  const casas = v < 0.01 ? 6 : 4;
  return `US$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })}`;
}

/** "2 apontamentos", "1 apontamento". */
export function plural(n: number, um: string, varios: string): string {
  return `${n.toLocaleString("pt-BR")} ${n === 1 ? um : varios}`;
}

/** O texto da busca sem acento e sem caixa, para comparar. */
export function semAcento(v: string | null | undefined): string {
  return (v ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** A busca das listas: o nome (e o fantasia), os dígitos do documento SÓ quando o termo tem dígito, e o dono.
 *  Lá, o termo sem dígito virava "" e `cnpj.includes("")` casava todo cadastro; na lista de CPFs, o regex era
 *  `/D/` (a letra), e a busca por CPF formatado não achava nada. */
export function casaNaBusca(termo: string, nomes: (string | null | undefined)[], documento: string | null | undefined): boolean {
  const t = semAcento(termo).trim();
  if (!t) return true;
  if (nomes.some((n) => semAcento(n).includes(t))) return true;
  const d = digitos(termo);
  return d.length > 0 && digitos(documento).includes(d);
}
